import cx from 'classnames';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box } from '@evaneos/b2b-ui';

import { Processes, StepName } from '../../../../design-system/types';
import { NEW_REQUEST_STEP } from '../../../../domain';
import {
    displayDossierOverviewsToIds,
    displayDossierOverviewsToTaskDefinitions,
} from '../../../../resources/dossier/operations';
import { DisplayDossierOverviews, DossierId } from '../../../../resources/dossier/types';
import { LoadMore } from '../../../../shared/components/LoadMore';
import s from '../../Process.module.css';
import { DraggableDossierOverview } from '../DraggableDossierOverview';
import { NewRequestDossierOverview } from '../NewRequestDossierOverview';
import { useHasMultipleMarkets } from '../../../../resources/pipeline/hooks';

import {
    ChangeStepMutationVariables,
    useChangeStepMutation,
} from '../../../../resources/dossier/mutations';

import { StepTitle } from './StepTitle';

export type Props = {
    canDrop: boolean;
    displayLoader: boolean;
    isMobile: boolean;
    isOver: boolean;
    isOverOrigin: boolean;
    latestUpdated: DossierId | null;
    name: StepName;
    process: Processes;
    setLatestUpdated: React.Dispatch<React.SetStateAction<Props['latestUpdated']>>;
    value: DisplayDossierOverviews;
    draggedDossierId: DossierId | null;
    showTotalBudgetLabel: boolean;
};

const itemIncrementCount = 20;

const StepBase = forwardRef<HTMLDivElement, Props>(
    (
        {
            canDrop,
            displayLoader,
            isMobile,
            isOver,
            isOverOrigin,
            name,
            process,
            latestUpdated,
            setLatestUpdated,
            value,
            showTotalBudgetLabel,
        },
        drop,
    ) => {
        const changeStepMutation = useChangeStepMutation();
        const { formatMessage } = useIntl();

        const stepContainerRef = useRef<HTMLDivElement>(null);
        const [lastIndexToSlice, setLastIndexToSlice] = useState(itemIncrementCount);

        const hasMultipleMarkets = useHasMultipleMarkets();

        const increaseLastIndexToSlice = () => {
            if (value.length > lastIndexToSlice) {
                setLastIndexToSlice((prev) => prev + itemIncrementCount);
            }
        };

        const setAndDelayRemoveLatestUpdatedDossier = (dossierId: DossierId): void => {
            setLatestUpdated(dossierId);
        };

        const handleRetry = ({ dossierId, nextStep }: ChangeStepMutationVariables) => {
            changeStepMutation.mutate({ dossierId, nextStep });
        };

        useEffect(() => {
            if (latestUpdated) {
                const element = document.getElementById(latestUpdated);
                if (!element) {
                    // if we have a lastestUpdated but not in the list, we need to increase the lastIndexToSlice to find it
                    increaseLastIndexToSlice();
                    return;
                }
                element?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps -- latestUpdated, value and lastIndexToSlice trigger the scroll, re-subscribing would reset scroll position
        }, [latestUpdated, value, lastIndexToSlice]);

        useEffect(() => {
            window.setTimeout(() => {
                setLatestUpdated(null);
            }, 2000);
        }, [latestUpdated, setLatestUpdated]);

        const total = value.length;

        const slicedList = [...value].slice(0, lastIndexToSlice);

        return (
            <div className={s.stepContainer} ref={drop}>
                <StepTitle
                    process={process}
                    name={name}
                    value={value}
                    total={total}
                    showTotalBudgetLabel={showTotalBudgetLabel}
                />
                <div
                    style={{ width: '316px' }}
                    ref={stepContainerRef}
                    data-testid={`process-step-${name}`}
                    className={cx(s.step, { [s.loading]: displayLoader })}
                >
                    {isOver && !isOverOrigin && canDrop && (
                        <div data-testid="dropzone-overlay" className={s.stepContainerOverlay}>
                            <FormattedMessage
                                id="steps.dropzone_message"
                                values={{
                                    step: formatMessage({
                                        id: `steps.${name}.name`,
                                    }),
                                }}
                            />
                        </div>
                    )}
                    <Box sx={{ position: 'relative', height: '100%', overflow: 'auto', p: 1 }}>
                        {name === NEW_REQUEST_STEP
                            ? slicedList.map((dossier) => {
                                  if (displayLoader) {
                                      return null;
                                  }
                                  return (
                                      <NewRequestDossierOverview
                                          key={dossier.id}
                                          doNotDrag={isMobile}
                                          isLatestUpdated={dossier.id === latestUpdated}
                                          onRetry={handleRetry}
                                          setLatestUpdated={setAndDelayRemoveLatestUpdatedDossier}
                                          dossier={dossier}
                                      />
                                  );
                              })
                            : slicedList.map((dossier) => {
                                  if (displayLoader) {
                                      return null;
                                  }
                                  return (
                                      <DraggableDossierOverview
                                          key={dossier.id}
                                          doNotDrag={isMobile}
                                          isLatestUpdated={dossier.id === latestUpdated}
                                          hasMultipleMarkets={hasMultipleMarkets}
                                          id={dossier.id}
                                          onRetry={handleRetry}
                                          setLatestUpdated={setAndDelayRemoveLatestUpdatedDossier}
                                          dossier={dossier}
                                      />
                                  );
                              })}
                        {value.length > lastIndexToSlice && (
                            <LoadMore
                                style={{ position: 'absolute', bottom: '10%' }}
                                onloadMore={increaseLastIndexToSlice}
                            />
                        )}
                    </Box>
                </div>
            </div>
        );
    },
);

export const Step = React.memo(StepBase, (prevProps, nextProps) => {
    const currentIds = displayDossierOverviewsToIds(prevProps.value);
    const nextIds = displayDossierOverviewsToIds(nextProps.value);
    const currentTaskDefinitions = displayDossierOverviewsToTaskDefinitions(prevProps.value);
    const nextTaskDefinitions = displayDossierOverviewsToTaskDefinitions(nextProps.value);
    const arraysEqual = (a: string[], b: string[]) =>
        a.length === b.length && a.every((val, index) => val === b[index]);
    return (
        (nextProps.isOverOrigin || prevProps.isOver === nextProps.isOver) &&
        arraysEqual(currentIds, nextIds) &&
        arraysEqual(currentTaskDefinitions, nextTaskDefinitions) &&
        prevProps.displayLoader === nextProps.displayLoader &&
        prevProps.showTotalBudgetLabel === nextProps.showTotalBudgetLabel
    );
});
